/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * Chat messages are stored as ProseMirror/TipTap documents rather than rendered HTML.
 * Everything derived from a message body -- search text, notification previews, the
 * sidebar last-message line -- is computed from that document here,
 * on both the client (optimistic UI) and the server (the values actually persisted).
 */

export interface ProseMirrorMark {
  type: string
  attrs?: Record<string, unknown>
}

export interface ProseMirrorNode {
  type: string
  text?: string
  attrs?: Record<string, unknown>
  marks?: ProseMirrorMark[]
  content?: ProseMirrorNode[]
}

export interface ProseMirrorDoc extends ProseMirrorNode {
  type: 'doc'
  content?: ProseMirrorNode[]
}

/**
 * Node types allowed in a stored chat document. `mention` is kept so older documents
 * still validate; the composer no longer produces them.
 */
export const CHAT_ALLOWED_NODE_TYPES = [
  'doc',
  'paragraph',
  'text',
  'hardBreak',
  'bulletList',
  'orderedList',
  'listItem',
  'mention',
  'emoji',
] as const

/** Mark types the chat composer is allowed to produce. */
export const CHAT_ALLOWED_MARK_TYPES = ['bold', 'italic', 'code', 'link'] as const

const ALLOWED_NODES = new Set<string>(CHAT_ALLOWED_NODE_TYPES)
const ALLOWED_MARKS = new Set<string>(CHAT_ALLOWED_MARK_TYPES)

/**
 * Only absolute http(s) URLs may be stored or rendered as links. `javascript:`,
 * `data:`, and protocol-relative values are rejected so a crafted document cannot
 * become stored XSS when another user clicks the mark.
 */
export const isSafeChatLinkHref = (href: unknown): href is string => {
  if (typeof href !== 'string') {
    return false
  }
  try {
    const url = new URL(href)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/** Node types that should be followed by a line break when flattened to text. */
const BLOCK_NODES = new Set(['paragraph', 'listItem', 'bulletList', 'orderedList'])

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Narrow unknown JSON (a GraphQL `JSONObject`, or a Prisma `Json` column) to a
 * ProseMirror document. Returns null when the shape is not a document at all, which
 * callers treat as a validation failure rather than an empty message.
 */
export const asProseMirrorDoc = (value: unknown): ProseMirrorDoc | null => {
  if (!isRecord(value) || value.type !== 'doc') {
    return null
  }
  if (value.content !== undefined && !Array.isArray(value.content)) {
    return null
  }
  return value as unknown as ProseMirrorDoc
}

/**
 * Walk every node in a document depth first. Used by the extractors below so they
 * all agree on traversal order, which is also the reading order of the message.
 */
const walk = (node: ProseMirrorNode, visit: (node: ProseMirrorNode) => void): void => {
  visit(node)
  if (!node.content) {
    return
  }
  for (const child of node.content) {
    if (isRecord(child)) {
      walk(child, visit)
    }
  }
}

/**
 * Reject documents containing node or mark types the composer cannot produce. The
 * content column is client-supplied JSON, so this is the boundary that keeps unknown
 * nodes -- and anything that would later be rendered -- out of the database.
 */
export const validateProseMirrorDoc = (doc: ProseMirrorDoc): { valid: true } | { valid: false; reason: string } => {
  let failure: string | null = null

  walk(doc, (node) => {
    if (failure) {
      return
    }
    if (typeof node.type !== 'string' || !ALLOWED_NODES.has(node.type)) {
      failure = `Unsupported node type: ${String(node.type)}`
      return
    }
    for (const mark of node.marks ?? []) {
      if (!ALLOWED_MARKS.has(mark.type)) {
        failure = `Unsupported mark type: ${String(mark.type)}`
        return
      }
      if (mark.type === 'link' && !isSafeChatLinkHref(mark.attrs?.href)) {
        failure = 'Invalid link URL'
        return
      }
    }
  })

  return failure ? { valid: false, reason: failure } : { valid: true }
}

/**
 * Flatten a document to plain text. This is what gets stored in
 * `ChatMessage.plainText` and therefore what full-text search matches on, so leftover
 * mention nodes contribute `@label` and emoji contribute the emoji character itself.
 */
export const proseMirrorToPlainText = (doc: ProseMirrorDoc | null): string => {
  if (!doc) {
    return ''
  }

  const parts: string[] = []

  const render = (node: ProseMirrorNode): void => {
    if (node.type === 'text') {
      parts.push(node.text ?? '')
      return
    }
    if (node.type === 'hardBreak') {
      parts.push('\n')
      return
    }
    if (node.type === 'mention') {
      const label = node.attrs?.label ?? node.attrs?.id
      parts.push(`@${typeof label === 'string' ? label : ''}`)
      return
    }
    if (node.type === 'emoji') {
      const emoji = node.attrs?.emoji ?? node.attrs?.name
      parts.push(typeof emoji === 'string' ? emoji : '')
      return
    }

    for (const child of node.content ?? []) {
      if (isRecord(child)) {
        render(child)
      }
    }

    if (BLOCK_NODES.has(node.type)) {
      parts.push('\n')
    }
  }

  render(doc)

  return parts
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** A document is empty when it flattens to no text. Media-only messages hit this. */
export const isEmptyProseMirrorDoc = (doc: ProseMirrorDoc | null): boolean => proseMirrorToPlainText(doc).length === 0

/**
 * Build the one-line preview shown in the sidebar and in notifications. Media-only
 * messages have no plain text, so callers pass a fallback label for that case.
 */
export const chatMessagePreview = (plainText: string, maxLength = 100): string => {
  const singleLine = plainText.replace(/\s+/g, ' ').trim()
  if (singleLine.length <= maxLength) {
    return singleLine
  }
  return `${singleLine.slice(0, maxLength - 1).trimEnd()}…`
}

const PARAGRAPH_BREAK = /\n{2,}/

/**
 * Wrap plain text in a minimal ProseMirror document. Used by the legacy Message
 * migration, which has plain strings rather than editor documents.
 */
export const plainTextToProseMirrorDoc = (text: string): ProseMirrorDoc => {
  const paragraphs: ProseMirrorNode[] = text
    .split(PARAGRAPH_BREAK)
    .map((block) => block.trim())
    .filter((block) => block.length > 0)
    .map((block) => ({
      type: 'paragraph',
      content: block.split('\n').flatMap((line, index) => {
        const textNode: ProseMirrorNode[] = line === '' ? [] : [{ type: 'text', text: line }]
        return index === 0 ? textNode : [{ type: 'hardBreak' }, ...textNode]
      }),
    }))

  // A doc with no content is not a valid ProseMirror document.
  return { type: 'doc', content: paragraphs.length > 0 ? paragraphs : [{ type: 'paragraph' }] }
}
